import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import { getCurrentAvailableLanguage } from '@/configuration/helpers/helpers'
import configurationServices from '@/configuration/services/services'

import {
  allCopies,
  buildMerchantInfo,
  flattenCustomCopies,
  focusOnError,
  getErrors,
  getPayload,
  initCopyValue,
  setToNullDisabledCopies,
} from '../helpers'

const useCopiesConfiguration = () => {
  const { t, i18n } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const returnPortalId = info?.selectedReturnPortal?.id
  const supportedLanguages = info?.selectedStore?.supported_languages || ['en']
  const language = getCurrentAvailableLanguage(supportedLanguages, i18n.language)

  const {
    data: customCopiesResponse,
    loading: gettingCustomCopies,
    fetchData: refetchCopies,
  } = useFetch(() => configurationServices.getPortalCustomCopies({ returnPortalId }), {
    doInitialRequest: !!returnPortalId,
  })

  const { returnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId,
    doInitialRequest: !!returnPortalId,
  })

  const { fetchData: saveCustomCopies, loading: savingCustomCopies } = useFetch(
    configurationServices.modifyPortalCustomCopies,
    {
      doInitialRequest: false,
    }
  )

  const [customCopies, setCustomCopies] = useState({})
  const [hasChanges, setHasChanges] = useState(false)
  const [copiesToSetAsDisabled, setCopiesToSetAsDisabled] = useState([])
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (customCopiesResponse) {
      setCustomCopies(flattenCustomCopies(customCopiesResponse, allCopies))
      setHasChanges(false)
    }
  }, [customCopiesResponse])

  useEffect(() => {
    if (copiesToSetAsDisabled.length) {
      setHasChanges(true)
    }
  }, [copiesToSetAsDisabled])

  const revertChanges = () => {
    setCustomCopies(flattenCustomCopies(customCopiesResponse, allCopies))
    setHasChanges(false)
    setCopiesToSetAsDisabled([])
  }

  const onChangeCopy = (value, fullKey, copyLanguage, attribute) => {
    setCustomCopies(prev => {
      const langCopies = { ...(prev[copyLanguage] || {}) }

      if (attribute) {
        const currentValue = langCopies[fullKey]
        const baseObject = currentValue && typeof currentValue === 'object' ? currentValue : {}
        langCopies[fullKey] = { ...baseObject, [attribute]: value }
      } else {
        langCopies[fullKey] = value
      }

      return { ...prev, [copyLanguage]: langCopies }
    })
    setHasChanges(true)
  }

  const initCustomCopy = fullKey => {
    const copy = allCopies.find(c => c.key === fullKey)
    if (!copy) return

    setCustomCopies(prev => {
      const next = { ...prev }
      supportedLanguages.forEach(lang => {
        next[lang] = { ...(next[lang] || {}), [fullKey]: initCopyValue(copy) }
      })
      return next
    })
  }

  const onChangeIsActive = (checked, fullKey) => {
    if (checked) {
      setCopiesToSetAsDisabled(prev => prev.filter(key => key !== fullKey))
      initCustomCopy(fullKey)
    } else {
      setCopiesToSetAsDisabled(prev => [...prev, fullKey])
    }
  }

  const isCustomCopyActive = fullKey => {
    for (const lang of supportedLanguages) {
      return lang in customCopies && fullKey in customCopies[lang] && !copiesToSetAsDisabled.includes(fullKey)
    }
  }

  const saveChanges = async () => {
    const updated = setToNullDisabledCopies(copiesToSetAsDisabled, supportedLanguages, customCopies, allCopies)
    const validationErrors = getErrors(updated, supportedLanguages, copiesToSetAsDisabled, allCopies)
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0) {
      const payload = getPayload(updated, allCopies)

      try {
        await saveCustomCopies({ returnPortalId, copies: payload })
        await refetchCopies()
        setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    } else {
      focusOnError(validationErrors)
    }
  }

  return {
    gettingCustomCopies: gettingCustomCopies || !customCopiesResponse,
    customCopies,
    savingCustomCopies,
    hasChanges,
    revertChanges,
    onChangeCopy,
    onChangeIsActive,
    isCustomCopyActive,
    errors,
    saveChanges,
    supportedLanguages,
    language,
    returnsPortalUrl: info?.selectedStore?.returns_portal_url,
    merchantInfo: buildMerchantInfo(info?.selectedStore, returnPortalFeatures?.contact_method),
    address: info?.address,
  }
}

export default useCopiesConfiguration
